import axios from "../utils/axios";

/**
 * List all skills with pagination and advanced filtering
 * @param {Object} params - Query parameters (page, per_page, search, trashed)
 * @returns {Promise} Skills list
 */
export const getSkills = async (params) => {
    try {
        const { data } = await axios.get('/skills', { params });
        return data;
    } catch (error) {
        console.error("🚀 ~ getSkills ~ error:", error);
        throw error;
    }
};

/**
 * Get a single skill by ID
 * @param {number|string} id - Skill ID
 * @returns {Promise} Skill data
 */
export const getSkillById = async (id) => {
    try {
        const { data } = await axios.get(`/skills/${id}`);
        return data;
    } catch (error) {
        console.error("🚀 ~ getSkillById ~ error:", error);
        throw error;
    }
};

/**
 * Create a new skill
 * @param {Object} skillData - Skill data (name_ar/en, bio_ar/en, description_ar/en, is_active, images)
 * @returns {Promise} Created skill
 */
export const createSkill = async (skillData) => {
    try {
        const { data } = await axios.post('/skills', skillData);
        return data;
    } catch (error) {
        console.error("🚀 ~ createSkill ~ error:", error);
        throw error;
    }
};

/**
 * Update an existing skill
 * @param {number|string} id - Skill ID
 * @param {Object} skillData - Updated skill data
 * @returns {Promise} Updated skill
 */
export const updateSkill = async (id, skillData) => {
    try {
        const { data } = await axios.put(`/skills/${id}`, skillData);
        return data;
    } catch (error) {
        console.error("🚀 ~ updateSkill ~ error:", error);
        throw error;
    }
};

/**
 * Soft delete a skill
 * @param {number|string} id - Skill ID
 * @returns {Promise} Result of deletion
 */
export const deleteSkill = async (id) => {
    try {
        const { data } = await axios.delete(`/skills/${id}`);
        return data;
    } catch (error) {
        console.error("🚀 ~ deleteSkill ~ error:", error);
        throw error;
    }
};

/**
 * Force delete a skill (permanent)
 * @param {number|string} id - Skill ID
 * @returns {Promise} Result of deletion
 */
export const forceDeleteSkill = async (id) => {
    try {
        const { data } = await axios.delete(`/skills/${id}/force-delete`);
        return data;
    } catch (error) {
        console.error("🚀 ~ forceDeleteSkill ~ error:", error);
        throw error;
    }
};

/**
 * Restore a soft-deleted skill
 * @param {number|string} id - Skill ID
 * @returns {Promise} Restored skill
 */
export const restoreSkill = async (id) => {
    try {
        const { data } = await axios.post(`/skills/${id}/restore`);
        return data;
    } catch (error) {
        console.error("🚀 ~ restoreSkill ~ error:", error);
        throw error;
    }
};

/**
 * Bulk soft delete skills
 * @param {Array} items - Array of skill IDs
 * @returns {Promise} Result
 */
export const massDestroySkills = async (data) => {
    try {
        const response = await axios.delete('/skills/mass-destroy', { data });
        return response.data;
    } catch (error) {
        console.error("🚀 ~ massDestroySkills ~ error:", error);
        throw error;
    }
};

/**
 * Bulk force delete skills
 * @param {Array} items - Array of skill IDs
 * @returns {Promise} Result
 */
export const massForceDeleteSkills = async (data) => {
    try {
        const response = await axios.delete('/skills/mass-force-delete', { data });
        return response.data;
    } catch (error) {
        console.error("🚀 ~ massForceDeleteSkills ~ error:", error);
        throw error;
    }
};

/**
 * Bulk restore skills
 * @param {Array} items - Array of skill IDs
 * @returns {Promise} Result
 */
export const massRestoreSkills = async (data) => {
    try {
        const response = await axios.post('/skills/mass-restore', data);
        return response.data;
    } catch (error) {
        console.error("🚀 ~ massRestoreSkills ~ error:", error);
        throw error;
    }
};